<template>
  <div class="glass-file-input" :class="{ 'has-error': hasError, 'is-disabled': disabled }">
    <label v-if="label" class="file-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>
    
    <div 
      class="file-drop-zone"
      :class="{ 'is-dragging': isDragging, 'has-file': hasFile }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="triggerInput"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        @change="onFileChange"
        class="file-input-hidden"
      />
      
      <div v-if="!hasFile" class="placeholder-content">
        <i :class="icon || 'fas fa-cloud-upload-alt'" class="upload-icon"></i>
        <span class="placeholder-text">{{ placeholder }}</span>
        <span class="placeholder-hint">{{ hint || 'Glissez-déposez ou cliquez pour sélectionner' }}</span>
      </div>
      
      <div v-else class="file-preview">
        <div v-for="(file, index) in files" :key="index" class="file-item">
          <i class="fas fa-file file-icon"></i>
          <span class="file-name">{{ file.name }}</span>
          <span class="file-size">{{ formatSize(file.size) }}</span>
          <button 
            type="button" 
            class="remove-btn"
            @click.stop="removeFile(index)"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
    
    <span v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'GlassFileInput',
  emits: ['update:modelValue', 'change'],
  props: {
    modelValue: {
      type: [File, Array],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Sélectionner un fichier'
    },
    hint: {
      type: String,
      default: ''
    },
    accept: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    maxSize: {
      type: Number,
      default: 0 // 0 = no limit
    }
  },
  data() {
    return {
      isDragging: false,
      files: []
    };
  },
  computed: {
    hasFile() {
      return this.files.length > 0;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (val) {
          this.files = Array.isArray(val) ? val : [val];
        } else {
          this.files = [];
        }
      }
    }
  },
  methods: {
    triggerInput() {
      if (!this.disabled) {
        this.$refs.fileInput.click();
      }
    },
    onDragOver() {
      if (!this.disabled) {
        this.isDragging = true;
      }
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;
      if (this.disabled) return;
      
      const files = Array.from(event.dataTransfer.files);
      this.handleFiles(files);
    },
    onFileChange(event) {
      const files = Array.from(event.target.files);
      this.handleFiles(files);
    },
    handleFiles(files) {
      if (!this.multiple) {
        this.files = files.slice(0, 1);
      } else {
        this.files = files;
      }
      
      const emitValue = this.multiple ? this.files : this.files[0] || null;
      this.$emit('update:modelValue', emitValue);
      this.$emit('change', emitValue);
    },
    removeFile(index) {
      this.files.splice(index, 1);
      const emitValue = this.multiple ? this.files : this.files[0] || null;
      this.$emit('update:modelValue', emitValue);
      this.$emit('change', emitValue);
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    clear() {
      this.files = [];
      this.$refs.fileInput.value = '';
      this.$emit('update:modelValue', null);
    }
  }
};
</script>

<style scoped>
.glass-file-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
}

.required {
  color: var(--color-danger);
  margin-left: 0.25rem;
}

.file-drop-zone {
  position: relative;
  padding: 2rem;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius);
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.file-drop-zone:hover:not(.is-disabled) {
  border-color: var(--color-primary);
  background: rgba(59, 136, 165, 0.1);
}

.file-drop-zone.is-dragging {
  border-color: var(--color-primary);
  background: rgba(59, 136, 165, 0.15);
  transform: scale(1.02);
}

.file-drop-zone.has-file {
  padding: 1rem;
  text-align: left;
}

.is-disabled .file-drop-zone {
  opacity: 0.6;
  cursor: not-allowed;
}

.file-input-hidden {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 2.5rem;
  color: var(--color-primary);
  opacity: 0.7;
}

.placeholder-text {
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text);
}

.placeholder-hint {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.file-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: calc(var(--border-radius) - 4px);
}

.file-icon {
  color: var(--color-primary);
}

.file-name {
  flex: 1;
  font-size: 0.875rem;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.remove-btn {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: var(--color-danger);
  color: white;
}

.has-error .file-drop-zone {
  border-color: var(--color-danger);
}

.error-message {
  font-size: 0.8125rem;
  color: var(--color-danger);
}
</style>
